import { useState } from 'react';
import { newClientRequestId } from '@lib/care-api';
import {
  MAX_REASON, MAX_WORD_REF, MIN_WORD_REF, UNSTOP_CARE_LINE_WORDING, lookupUnstop, type UnstopBasis,
} from '@lib/quick-keys';
import { isStagedAnswer } from '@lib/staged-presses';

/**
 * THE LOOKUP'S UNSTOP (William's BB1, about 5:10 AM 22 Oct 2026: "if care goes into the account (e.g., lookup) and then
 * un-stops the account"). Shown on a Lookup member card that shows the member's STOP, only while the server says the
 * Unstop is Care's own line (features.unstopCareLine: MEMBERCARE_UNSTOP_CARE_LINE on, the master link off). The press
 * names the member; the server queues it on the member's newest open case for Final Review, and the approval writes
 * the care line CLEAR of STOP with its lift_basis, a case note and an access-log row. Nothing acts at the press.
 *
 * NO em dashes (house voice rule). ASCII hyphens only.
 */
export function LookupUnstop({ memberId }: { memberId: string }) {
  const [open, setOpen] = useState(false);
  const [crid, setCrid] = useState('');
  const [basis, setBasis] = useState<UnstopBasis | ''>('');
  const [wordRef, setWordRef] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  const need = !basis ? 'Say what lifts the STOP: the member texted START, William\'s word, or Care staff with a reason.'
    : !reason.trim() ? 'Give the reason: it is the record of why.'
      : basis === 'his_word' && (wordRef.trim().length < MIN_WORD_REF || wordRef.trim().length > MAX_WORD_REF) ? 'Cite his word: where and when he said it.'
        : '';

  const press = async () => {
    setBusy(true);
    setResult(null);
    try {
      const r = await lookupUnstop({
        memberId, basis: basis as UnstopBasis, reason: reason.trim(), clientRequestId: crid,
        ...(basis === 'his_word' ? { wordRef: wordRef.trim() } : {}),
      });
      if (isStagedAnswer(r)) {
        setResult({ ok: true, text: `Queued for approval: Unstop on case ${String((r.data as { eventId?: unknown }).eventId ?? '')}. It takes effect when the case is approved in Final Review.` });
        setOpen(false);
        return;
      }
      const code = String((r.data as { code?: unknown }).code ?? '');
      setResult({ ok: false, text: code === 'no_open_case' ? UNSTOP_CARE_LINE_WORDING.lookupNoCase : String((r.data as { error?: unknown; message?: unknown }).error ?? (r.data as { message?: unknown }).message ?? `HTTP ${r.status}`) });
      setCrid(newClientRequestId());
    } catch (err) {
      setResult({ ok: false, text: `The server did not answer (${(err as Error).message}). Press again: the same press is sent, never twice.` });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="note" style={{ marginBottom: 8 }} data-testid="lookup-unstop">
      {!open ? (
        <button type="button" className="btn btn-sm btn-ghost" onClick={() => { setOpen(true); setCrid(newClientRequestId()); setResult(null); }}>
          Unstop
        </button>
      ) : (
        <>
          <div><strong>Unstop</strong> <span className="muted">{UNSTOP_CARE_LINE_WORDING.waits}</span></div>
          <fieldset style={{ border: 0, padding: 0, margin: '8px 0 0' }}>
            <legend className="micro muted">What lifts the STOP</legend>
            <label style={{ display: 'block' }}>
              <input type="radio" name="lu-basis" checked={basis === 'member_start'} onChange={() => setBasis('member_start')} /> The member texted START (Care checks its record)
            </label>
            <label style={{ display: 'block' }}>
              <input type="radio" name="lu-basis" checked={basis === 'his_word'} onChange={() => setBasis('his_word')} /> William&apos;s word
            </label>
            <label style={{ display: 'block' }}>
              <input type="radio" name="lu-basis" checked={basis === 'staff'} onChange={() => setBasis('staff')} /> {UNSTOP_CARE_LINE_WORDING.staff}
            </label>
            {basis === 'his_word' && (
              <input className="text-input" aria-label="Where he said it" placeholder="Where and when he said it" value={wordRef}
                maxLength={MAX_WORD_REF} onChange={(e) => setWordRef(e.target.value)} style={{ marginTop: 4, width: '100%' }} />
            )}
          </fieldset>
          <input className="text-input" aria-label="Reason" value={reason} maxLength={MAX_REASON} onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (required): why" style={{ marginTop: 8, width: '100%' }} />
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-sm btn-primary" disabled={busy || !!need} title={need || undefined} onClick={() => void press()}>
              {busy ? 'Pressing...' : 'Send Unstop to Final Review'}
            </button>
            <button type="button" className="btn btn-sm btn-ghost" disabled={busy} onClick={() => setOpen(false)}>Cancel</button>
            {need && <span className="micro muted" data-testid="lookup-unstop-need">{need}</span>}
          </div>
        </>
      )}
      {result && <div className={result.ok ? 'pill' : 'pill pill-warning'} style={{ display: 'block', marginTop: 6, whiteSpace: 'normal' }} data-testid="lookup-unstop-result">{result.text}</div>}
    </div>
  );
}
